import {
  Component, ElementRef,
  EventEmitter, HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges, ViewChild
} from '@angular/core';
import { FormControl } from "@angular/forms";
import { debounceTime, distinctUntilChanged } from "rxjs";
import { WorkOrder, WorkOrderSearch, WorkOrderSearchTypes } from "@shared/models/work-order";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
    selector: 'app-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
    standalone: false
})
export class SearchComponent implements OnInit, OnChanges {

  @ViewChild('searchContainer') searchContainer!: ElementRef;
  @Input() workOrders: WorkOrder[] = [];
  @Input() inverters: WorkOrder[] = [];
  @Input() type3WorkOrders: WorkOrder[] = [];
  @Input() powerLineWorkOrders: WorkOrder[] = [];
  @Input() strictSearch = true;
  @Input() maxResults = 100;
  @Input() reset = false;
  @Input() showExtraInfo = true;
  @Input() resetWhenClickedOutside = false;
  @Output() onChange = new EventEmitter<WorkOrderSearch[]>();
  @Output() onClick = new EventEmitter<WorkOrderSearch>();
  @Output() onLocationClick = new EventEmitter<WorkOrderSearch>();

  private combinedWorkOrders: WorkOrderSearch[] = [];
  public searchControl = new FormControl('');
  public searchText: string = '';
  public workOrdersFiltered: WorkOrderSearch[] = [];
  public searchTypes = WorkOrderSearchTypes;

  constructor() {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(value => {
        if (!this.strictSearch && !value) {
          this.close();
          this.onChange.emit([]);
          return;
        }

        this.searchText = this.processSearchValue(value);
        this.workOrdersFiltered = this.strictSearch
          ? this.filterStrict(this.searchText)
          : this.filter(this.searchText);

        this.onChange.emit(this.workOrdersFiltered);
    });
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reset']) {
      this.close();
      return;
    }

    this.combineData();
  }

  private combineData(): void {
    this.combinedWorkOrders = [
      ...this.workOrders.map(order => ({ ...order, search_type: 'STANDARD' }) as WorkOrderSearch),
      ...this.inverters.map(order => ({ ...order, search_type: 'INVERTER' }) as WorkOrderSearch),
      ...this.type3WorkOrders.map(order => ({ ...order, search_type: 'TYPE3' }) as WorkOrderSearch),
      ...this.powerLineWorkOrders.map(order => ({ ...order, search_type: 'POWERLINE' }) as WorkOrderSearch),
    ];
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.resetWhenClickedOutside) return;

    if (
      this.searchContainer &&
      !this.searchContainer.nativeElement.contains(event.target)
    ) {
      this.close();
    }
  }

  private processSearchValue(search: string | null): string {
    return search?.trim() ?? '';
  }

  private filter(search: string): WorkOrderSearch[] {
    if (!search) return [];

    return this.combinedWorkOrders.filter(
      workOrder => workOrder.identifier?.toLowerCase()?.indexOf(search.toLowerCase()) !== -1
        || (this.showExtraInfo
          && (
            ((`${workOrder.identifier} - ${workOrder.transformer} - ${workOrder.inverter} - ${workOrder.circuit}`)
              ?.toLowerCase().indexOf(search.toLowerCase()) !== -1)
              || ((`${workOrder.identifier} ${workOrder.transformer} ${workOrder.inverter} ${workOrder.circuit}`)
                ?.toLowerCase().indexOf(search.toLowerCase()) !== -1)
          ))
    );
  }

  private filterStrict(search: string): WorkOrderSearch[] {
    return this.combinedWorkOrders.filter(
      workOrder => workOrder.identifier === search
    );
  }

  searchResultsCount(): number
  {
    return this.workOrdersFiltered.length;
  }

  click(workOrder: WorkOrderSearch): void {
    this.onClick.emit(workOrder);
  }

  close(): void {
    this.searchControl.setValue('');
    this.searchText = '';
    this.workOrdersFiltered = [];
  }

  goToLocation(workOrder: WorkOrderSearch): void {
    this.onLocationClick.emit(workOrder);
  }

}
